// Copy driven by the approval setting (HANDOFF.md "Approval setting drives the copy").
import { useEffect, useState } from "react";
import type { Settings } from "../api/types";
import { fmtDay, fmtTime, parseDate } from "../shared/format";

export type Policy = Settings["release"]["policy"];

export function policyWord(policy: Policy): "now" | "set" | "manual" {
  if (policy === "Immediate") return "now";
  if (policy === "Scheduled") return "set";
  return "manual";
}

/** "Approved automatically within 5 minutes – next at 2:35 PM" / "Will be approved automatically in Syncore today at 5:00 PM" / "Waiting for someone to approve them". */
export const ROLLING_MINUTES = 5;

export function approveLine(policy: Policy, nextRun: string | null, now?: Date): string {
  const mode = policyWord(policy);
  if (mode === "manual") return "Waiting for someone to approve them";
  const d = parseDate(nextRun);
  // Within the hour the time left is what people want to know; a daily batch keeps its clock time.
  if (d && now) {
    const ms = d.getTime() - now.getTime();
    if (ms <= 0) return "Approving now…";
    if (ms < 3600_000) return `Next automatic approval in ${countdown(ms)}`;
  }
  if (mode === "now") return `Approved automatically within ${ROLLING_MINUTES} minutes of matching` + (d ? ` – next at ${fmtTime(d)}` : "");
  if (!d) return "Will be approved automatically in Syncore at the next scheduled time";
  const day = fmtDay(d);
  return `Will be approved automatically in Syncore ${day === "Today" ? "today" : day === "Yesterday" ? "today" : "on " + day} at ${fmtTime(d)}`;
}

/** "4:12", "0:42": minutes and seconds until a run. */
export function countdown(ms: number): string {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The current time, ticking every `ms` while the tab is visible; for countdowns and "ago" times. */
export function useNow(ms = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let iv: number | null = null;
    const start = () => { if (iv === null) { setNow(new Date()); iv = window.setInterval(() => setNow(new Date()), ms); } };
    const stop = () => { if (iv !== null) { window.clearInterval(iv); iv = null; } };
    const onVisible = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener("visibilitychange", onVisible);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisible); };
  }, [ms]);
  return now;
}

/** The schedule in words: "Every day at 5:00 PM", "Weekdays at 5:00 PM", "Every hour", or the cron itself. */
export function scheduleWords(cron: string | null | undefined): string {
  if (!cron) return "At set times";
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return cron;
  const [minute, hour, , , dow] = parts;
  if (hour === "*" && /^\d+$/.test(minute)) return "Every hour";
  if (/^\d+$/.test(hour) && /^\d+$/.test(minute)) {
    const time = fmtTime(new Date(2000, 0, 1, Number(hour), Number(minute)));
    if (dow === "*" || dow === "?") return `Every day at ${time}`;
    if (dow === "1-5" || dow === "MON-FRI") return `Weekdays at ${time}`;
    return `${dow} at ${time}`;
  }
  return cron;
}

export interface ScheduleChoice {
  freq: "Every day" | "Weekdays" | "Every hour";
  time: string;
}

const TIMES = ["9:00 AM", "12:00 PM", "3:00 PM", "5:00 PM", "6:00 PM"];
export const SCHEDULE_TIMES = TIMES;

export function toCron(choice: ScheduleChoice): string {
  if (choice.freq === "Every hour") return "0 * * * *";
  const [time, ampm] = choice.time.split(" ");
  const [h, m] = time.split(":").map(Number);
  const hour = ampm === "PM" && h !== 12 ? h + 12 : ampm === "AM" && h === 12 ? 0 : h;
  return `${m} ${hour} * * ${choice.freq === "Weekdays" ? "1-5" : "*"}`;
}

export function fromCron(cron: string | null | undefined): ScheduleChoice {
  if (!cron) return { freq: "Every day", time: "5:00 PM" };
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return { freq: "Every day", time: "5:00 PM" };
  const [minute, hour, , , dow] = parts;
  if (hour === "*") return { freq: "Every hour", time: "5:00 PM" };
  const time = /^\d+$/.test(hour) && /^\d+$/.test(minute) ? fmtTime(new Date(2000, 0, 1, Number(hour), Number(minute))) : "5:00 PM";
  return { freq: dow === "1-5" || dow === "MON-FRI" ? "Weekdays" : "Every day", time };
}
